import type { DetectOptions, DetectionResult, ExecFileSync, ParentProcessInfo } from './types';
import { execFileSync as nodeExecFileSync } from 'node:child_process';
import { getColorSupport } from './color';
import { normalizeIdentifier, normalizeParentProcessName, SHELL_PROCESS_NAMES } from './normalize';

const runPs = (run: ExecFileSync, args: string[]): string => {
  return run('ps', args, {
    encoding: 'utf8',
    timeout: 1000,
    stdio: ['ignore', 'pipe', 'ignore']
  }) as string;
};

export const getParentProcess = (
  pid: number,
  options: Pick<DetectOptions, 'execFileSync'> = {}
): ParentProcessInfo | null => {
  const run = options.execFileSync ?? nodeExecFileSync;
  const parentPid = Number.parseInt(
    runPs(run, ['-o', 'ppid=', '-p', String(pid)]).trim(),
    10
  );

  if (!Number.isFinite(parentPid) || parentPid <= 1) {
    return null;
  }

  const appName = runPs(run, ['-o', 'comm=', '-p', String(parentPid)]).trim();

  if (!appName) {
    return null;
  }

  return {
    pid: parentPid,
    appName
  };
};

const colorVariant = (
  terminal: string,
  supports256Color: boolean,
  supportsTrueColor: boolean
): string => {
  return supports256Color || supportsTrueColor ? `${terminal}_256color` : terminal;
};

const applyParentColorVariant = (terminal: string, env: NodeJS.ProcessEnv): string => {
  const { supports256Color, supportsTrueColor } = getColorSupport(env);

  switch (terminal) {
    case 'eterm':
    case 'konsole':
    case 'rxvt':
      return colorVariant(terminal, supports256Color, supportsTrueColor);
    case 'gnome_terminal':
    case 'terminator':
      return colorVariant('gnome_terminal', supports256Color, supportsTrueColor);
    default:
      return terminal;
  }
};

export const detectFromParentProcessResult = (
  options: DetectOptions = {}
): DetectionResult | null => {
  const env = options.env ?? process.env;
  const platform = options.platform ?? process.platform;

  if (platform === 'win32' || env.SSH_CONNECTION) {
    return null;
  }

  const seenPids = new Set<number>();
  let pid = options.pid ?? process.pid;

  for (let depth = 0; depth < 12; depth += 1) {
    if (seenPids.has(pid)) {
      break;
    }

    seenPids.add(pid);

    try {
      const parent = getParentProcess(pid, options);

      if (!parent) {
        break;
      }

      const normalized = normalizeParentProcessName(parent.appName);

      if (normalized === 'screen' || normalized === 'tmux') {
        if (!options.preferOuter) {
          return {
            terminal: normalized,
            source: 'parent',
            appName: parent.appName,
            pid: parent.pid
          };
        }

        pid = parent.pid;
        continue;
      }

      if (normalized) {
        return {
          terminal: applyParentColorVariant(normalized, env),
          source: 'parent',
          appName: parent.appName,
          pid: parent.pid
        };
      }

      if (!SHELL_PROCESS_NAMES.has(normalizeIdentifier(parent.appName))) {
        break;
      }

      pid = parent.pid;
    } catch {
      break;
    }
  }

  return null;
};

export const detectFromParentProcess = (options: DetectOptions = {}): string | null => {
  return detectFromParentProcessResult(options)?.terminal ?? null;
};
